import { AnyAction, Middleware, MiddlewareAPI } from "redux"

import { loadAllReferenceableData } from "../cached-data/actions"
import { AppDispatch, LaborRateMatch, ReduxState } from "../common/types"
import { getFlagEnabled } from "../getFlagValue"

/**
 * Redux middleware that automatically fetches related labor rates when
 * laborRateMatch entities arrive in the store.
 *
 * Each LaborRateMatch references a labor_rate and/or labor_rate_override by ID.
 * This middleware watches for actions that deliver laborRateMatch entities and
 * dispatches a bulk fetch for any referenced labor rate IDs that are not
 * already in the laborRates cache.
 *
 * Gated behind the WA-9079-wage-rates feature flag.
 */
const laborRateMatchRelationsMiddleware: Middleware = (store: MiddlewareAPI<AppDispatch, ReduxState>) => (
    next: AppDispatch
) => (action: AnyAction) => {
    const result = next(action)

    const newLaborRateMatches = action?.payload?.response?.entities?.laborRateMatches as
        | Record<number, LaborRateMatch>
        | undefined

    if (!newLaborRateMatches) {
        return result
    }

    if (!getFlagEnabled("WA-9079-wage-rates")) {
        return result
    }

    // Collect all referenced labor rate IDs from the incoming matches
    const laborRateIds = new Set<number>()
    Object.values(newLaborRateMatches).forEach(match => {
        if (match.labor_rate) laborRateIds.add(match.labor_rate)
        if (match.labor_rate_override) laborRateIds.add(match.labor_rate_override)
    })

    if (laborRateIds.size > 0) {
        // loadAllReferenceableData is cache-aware — it skips IDs already present
        store.dispatch(loadAllReferenceableData("laborRates", [...laborRateIds]))
    }

    return result
}

export default laborRateMatchRelationsMiddleware
